// RecordContact.tsx — one contact form, used by the case record and by the call centre.
//
// EXTRACTED rather than copied. Both screens ask the same five questions and both must enforce the
// same boundary; two copies would answer the same question differently within a release or two,
// and the question is "what may a call centre agent do to a resident's case".
//

import { useState } from 'react'
import { api, ApiError } from '../../lib/api'


/**
 * The call centre's whole journey: find the resident, read what everyone else already knows, log
 * what was said.
 *
 * What it deliberately CANNOT do is as much the point as what it can. There is no category here,
 * no suppression, no letter. An agent hearing "I cannot pay" records `claims_hardship`, which the
 * server turns into a referral for assessment — not into a Category 1 classification. Hardship
 * suppresses letters and fees, so it needs evidence and a named officer behind it, and a call
 * handler under average-handling-time pressure is the wrong place to put that decision.
 *
 * `effects` is rendered from the server's reply and nothing else, exactly as the field app does.
 * The agent is the person who has to tell the resident what happens next, so they are handed what
 * actually happened rather than what the screen assumes.
 */
const CHANNELS = [
  { key: 'call_in', label: 'Resident called in' },
  { key: 'call_out', label: 'We called out' },
  { key: 'counter', label: 'Came to the counter' },
  { key: 'email', label: 'Email' },
  { key: 'whatsapp', label: 'WhatsApp' },
]

const CONTACT_OUTCOMES = [
  { key: 'promise_to_pay', label: 'Promised to pay' },
  { key: 'requests_plan', label: 'Wants an instalment plan' },
  { key: 'claims_hardship', label: 'Says they cannot pay' },
  { key: 'disputes_balance', label: 'Disputes the amount' },
  { key: 'will_call_back', label: 'Will call back' },
  { key: 'information_only', label: 'Information only' },
  { key: 'no_answer', label: 'No answer' },
  { key: 'wrong_number', label: 'Wrong number' },
]

export default function RecordContact({ org, caseId, resident, onSaved, startOpen = false }:
  { org: string; caseId: string; resident: string; onSaved: () => void
    /** The call centre opens straight into the form — recording the call IS the job there, and a
     *  closed prompt would be one tap in front of every single call. On a case record it stays
     *  shut, because most visits to that page are to READ the history. */
    startOpen?: boolean }) {
  const [open, setOpen] = useState(startOpen)
  const [channel, setChannel] = useState('call_in')
  const [outcome, setOutcome] = useState('')
  const [spokeTo, setSpokeTo] = useState('')
  const [promiseOn, setPromiseOn] = useState('')
  const [promiseAmt, setPromiseAmt] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState<Array<{ kind: string; summary: string; detail: string | null }> | null>(null)

  // A promise with no date cannot be followed up, so it is not a promise. The server refuses it
  // too — this only saves the agent a round trip while the resident is still on the line.
  const ready = !!outcome && (outcome !== 'promise_to_pay' || !!promiseOn)

  async function save() {
    setBusy(true); setErr(null)
    try {
      const r = await api.ams.logContact(org, caseId, {
        channel, outcome,
        notes: notes || null,
        spokeTo: spokeTo || null,
        promiseDate: outcome === 'promise_to_pay' ? promiseOn : null,
        promiseAmountCents: outcome === 'promise_to_pay' && promiseAmt
          ? Math.round(parseFloat(promiseAmt) * 100) : null,
      })
      setDone(r.effects)
      setOutcome(''); setNotes(''); setSpokeTo(''); setPromiseOn(''); setPromiseAmt('')
      onSaved()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : String(e))
    } finally { setBusy(false) }
  }

  if (!open) {
    return (
      <section className="card rc-shut">
        <div>
          <h3>Record a conversation</h3>
          <p className="muted sm">
            A call, a counter visit or a message. It goes on the record below and changes nothing
            else — no category, no letter.
          </p>
        </div>
        <button className="btn pri" onClick={() => { setOpen(true); setDone(null) }}>
          Record a conversation
        </button>
      </section>
    )
  }

  return (
    <section className="card">
      <h3>Record a conversation with {resident}</h3>

      {done && (
        <div className="notice ok rc-done">
          <b>Recorded.</b>
          {done.length === 0
            ? ' It is on the record. Nothing else changed.'
            : <ul className="fld-effects">
                {done.map((e, i) => (
                  <li key={i}><b>{e.summary}</b>
                    {e.detail && <div className="muted sm">{e.detail}</div>}</li>
                ))}
              </ul>}
        </div>
      )}

      <label className="f">How did they reach us?</label>
      <div className="chips">
        {CHANNELS.map(c => (
          <button key={c.key} className={`chip ${channel === c.key ? 'on' : ''}`}
                  onClick={() => setChannel(c.key)}>{c.label}</button>
        ))}
      </div>

      <label className="f">What was the outcome?</label>
      <div className="chips">
        {CONTACT_OUTCOMES.map(o => (
          <button key={o.key} className={`chip ${outcome === o.key ? 'on' : ''}`}
                  onClick={() => setOutcome(o.key)}>{o.label}</button>
        ))}
      </div>

      {outcome === 'claims_hardship' && (
        <p className="notice rc-note">
          This is recorded as something the resident <b>said</b>. It does not set a hardship
          category — an officer does that with evidence — but it does raise a referral for
          assessment.
        </p>
      )}

      {outcome === 'promise_to_pay' && (
        <div className="field-row rc-row">
          <div className="field">
            <label className="f">Date they gave</label>
            <input type="date" value={promiseOn} onChange={e => setPromiseOn(e.target.value)} />
          </div>
          <div className="field">
            <label className="f">Amount, if stated</label>
            <input inputMode="decimal" placeholder="e.g. 120.00" value={promiseAmt}
                   onChange={e => setPromiseAmt(e.target.value)} />
          </div>
        </div>
      )}

      <label className="f">Who did you speak to? <span className="muted">Often not the account holder</span></label>
      <input value={spokeTo} onChange={e => setSpokeTo(e.target.value)}
             placeholder={`e.g. ${resident}, or "daughter, Ms Ng"`} />

      <label className="f">What did they say?</label>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
                placeholder="In their words. This is what the next officer reads before they knock." />

      {err && <p className="notice stop rc-note">Not saved: {err}</p>}

      <div className="actions rc-actions">
        <button className="btn pri" disabled={!ready || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save to the record'}
        </button>
        <button className="btn" onClick={() => setOpen(false)}>Close</button>
      </div>
    </section>
  )
}
